import React, { useContext, useEffect, useState } from 'react'
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  Box,
  Typography,
  styled,
} from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { getValue, ensureInitialized } from 'firebase/remote-config'
import ModalVideo from 'react-modal-video'
import LaunchIcon from '@mui/icons-material/Launch'
import SendIcon from '@mui/icons-material/Send'
import recordIcon from '../../Assets/Images/HelpSupportShowcaseBg.png'
import { analytics, remoteConfig } from '../../firebase/firebase'
import { AuthContext } from '../../Context/AuthContext'
import { RemoteConfigKeys } from '../../firebase/remoteConfig'
import 'react-modal-video/scss/modal-video.scss'
import WhatsappIcon from '../../Assets/Images/whatsapp.svg'

// Styled components
const Container = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
})

const ShowcaseContainer = styled(Box)({
  position: 'relative',
  padding: '4rem 2rem',
  textAlign: 'center',
  background: 'linear-gradient(135deg, #6484e4 0%, #4a6cf7 100%)',
  color: 'white',
  overflow: 'hidden',
})

const ShowcaseBgImg = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  opacity: 0.1,
  zIndex: 1,
})

const ShowcaseContent = styled(Box)({
  position: 'relative',
  zIndex: 2,
})

const ShowcaseHeader = styled(Typography)({
  fontSize: '3rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
})

const ShowcasePara = styled(Typography)({
  fontSize: '1.2rem',
  opacity: 0.9,
})

const ContentContainer = styled(Box)({
  maxWidth: '1500px',
  margin: '0 auto',
  padding: '3rem 2rem',
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  gap: '3rem',
  alignItems: 'start',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
    gap: '2rem',
  },
})

const ContentContainerFaq = styled(Box)({
  backgroundColor: 'white',
  overflowY: 'auto',
  maxHeight: '600px',
  borderRadius: '12px',
  padding: '2rem',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
})

const Separator = styled(Box)({
  width: '2px',
  backgroundColor: '#e0e0e0',
  minHeight: '400px',
  '@media (max-width: 768px)': {
    display: 'none',
  },
})

const ContentContainerOther = styled(Box)({
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '2rem',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
})

const SectionTitle = styled(Typography)({
  fontSize: '1.5rem',
  fontWeight: '600',
  marginBottom: '1.5rem',
  color: '#333',
})

const FooterLinkHeader = styled(Typography)({
  fontSize: '1.2rem',
  fontWeight: '600',
  marginTop: '2rem',
  marginBottom: '1rem',
  color: '#333',
})

const FooterLink = styled('a')({
  display: 'block',
  color: '#6484e4',
  textDecoration: 'none',
  marginBottom: '0.5rem',
  fontSize: '1rem',
  '&:hover': {
    textDecoration: 'underline',
  },
})

const FooterAddress = styled(Typography)({
  fontSize: '0.9rem',
  color: '#666',
  lineHeight: '1.5',
  marginBottom: '0.5rem',
})

const OtherHeaderContainer = styled(Box)({
  marginBottom: '2rem',
})

const Para = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.5rem',
  marginBottom: '2rem',
  fontSize: '1rem',
  color: '#666',
})

const HelpAndSupport = () => {
  const { authState } = useContext(AuthContext)
  const [tutorFaqs, setTutorFaqs] = useState([])
  const [studentFaqs, setStudentFaqs] = useState([])
  const [launchPlayer, setLaunchPayer] = useState({ video: '', launch: false })
  const policiesBaseUrl = 'https://policies.edvi.app'

  useEffect(() => {
    analytics.setUserProperties({
      name: authState.name,
      email: authState.email,
      role: authState.role,
      phone_number: authState.phone_number,
    })
  }, [])

  const fetchRemoteValues = () => {
    ensureInitialized(remoteConfig).then(() => {
      const remoteConfigValue = getValue(
        remoteConfig,
        RemoteConfigKeys.FAQS,
      ).asString()
      const configFaqs = JSON.parse(
        !remoteConfigValue ? '{}' : remoteConfigValue,
      )
      const tutorFaqsConfig =
        'tutor_faqs' in configFaqs ? configFaqs.tutor_faqs : []
      const studentFaqsConfig =
        'student_faqs' in configFaqs ? configFaqs.student_faqs : []
      if (tutorFaqsConfig !== null && tutorFaqsConfig.length !== 0) {
        setTutorFaqs(tutorFaqsConfig)
      }
      if (studentFaqsConfig !== null && studentFaqsConfig.length !== 0) {
        setStudentFaqs(studentFaqsConfig)
      }
    }, null)
  }

  useEffect(() => {
    fetchRemoteValues()
  }, [authState.role])

  return (
    <Container>
      <ModalVideo
        channel="youtube"
        autoplay
        isOpen={launchPlayer.launch}
        videoId={launchPlayer.video}
        onClose={() => setLaunchPayer({ video: '', launch: false })}
      />

      <ShowcaseContainer>
        <ShowcaseBgImg>
          <img
            src={recordIcon || '/placeholder.svg'}
            alt="edvi logo"
            height={500}
            width={500}
          />
        </ShowcaseBgImg>
        <ShowcaseContent>
          <ShowcaseHeader variant="h1">Help and Support</ShowcaseHeader>
          <ShowcasePara variant="h6">
            Hi, How can we help you today?
          </ShowcasePara>
        </ShowcaseContent>
      </ShowcaseContainer>

      <ContentContainer>
        <ContentContainerFaq>
          <SectionTitle>Frequently Asked Questions</SectionTitle>

          {(authState === null && authState.role === null) ||
            (authState.role === '' && (
              <FooterLinkHeader>Tutor Faqs</FooterLinkHeader>
            ))}

          {(authState.role === 'T' ||
            authState === null ||
            authState.role === null ||
            authState.role === '') && (
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {tutorFaqs.map((value) => {
                const labelId = `checkbox-list-secondary-label-${value.id}`
                return (
                  <ListItem
                    key={value.id}
                    secondaryAction={<ArrowForwardIosIcon />}
                    disablePadding
                    onClick={() => {
                      setLaunchPayer({
                        video: `${new URLSearchParams(
                          new URL(value.video_url).search,
                        ).get('v')}`,
                        launch: true,
                      })
                    }}
                    sx={{
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <ListItemButton>
                      <ListItemAvatar>
                        <Avatar
                          alt={`Avatar n°${value.id}`}
                          src={`https://img.youtube.com/vi/${new URLSearchParams(
                            new URL(value.video_url).search,
                          ).get('v')}/default.jpg`}
                          variant="rounded"
                        />
                      </ListItemAvatar>
                      <ListItemText id={labelId} primary={value.action_title} />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          )}

          {(authState === null && authState.role === null) ||
            (authState.role === '' && (
              <FooterLinkHeader>Student Faqs</FooterLinkHeader>
            ))}

          {(authState.role === 'S' ||
            authState === null ||
            authState.role === null ||
            authState.role === '') && (
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {studentFaqs.map((value) => {
                const labelId = `checkbox-list-secondary-label-${value.id}`
                return (
                  <ListItem
                    key={value.id}
                    secondaryAction={<ArrowForwardIosIcon />}
                    disablePadding
                    onClick={() => {
                      setLaunchPayer({
                        video: `${new URLSearchParams(
                          new URL(value.video_url).search,
                        ).get('v')}`,
                        launch: true,
                      })
                    }}
                    sx={{
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <ListItemButton>
                      <ListItemAvatar>
                        <Avatar
                          alt={`Avatar n°${value.id}`}
                          src={`https://img.youtube.com/vi/${new URLSearchParams(
                            new URL(value.video_url).search,
                          ).get('v')}/default.jpg`}
                          variant="rounded"
                        />
                      </ListItemAvatar>
                      <ListItemText id={labelId} primary={value.action_title} />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          )}
        </ContentContainerFaq>

        <Separator />

        <ContentContainerOther>
          <SectionTitle>Still having query?</SectionTitle>

          <OtherHeaderContainer>
            <Button
              variant="outlined"
              endIcon={<SendIcon />}
              onClick={() => {
                if (window.$chatwoot) {
                  window.$chatwoot.toggle()
                } else {
                  console.warn('Chatwoot is not initialized')
                  // Optionally initialize Chatwoot here or show a message to the user
                  // window.open('https://your-support-page.com', '_blank')
                }
              }}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '1rem',
                padding: '0.75rem 1.5rem',
              }}
            >
              Connect now
            </Button>
          </OtherHeaderContainer>

          <Para>
            <span>Call or WhatsApp </span>
            <img
              src={WhatsappIcon || '/placeholder.svg'}
              alt="Whatsapp"
              style={{
                position: 'relative',
                top: '3px',
                width: '20px',
                height: '20px',
              }}
            />
            <a style={{ color: '#666' }} href="tel:+919569768932">
              {' '}
              at
              <span style={{ color: '#3458a1' }}> +919569768932</span>
              <LaunchIcon
                sx={{
                  position: 'relative',
                  top: '5px',
                  fontSize: '20px',
                  color: '#3458a1',
                }}
              />
            </a>
          </Para>

          <FooterLinkHeader>Policies</FooterLinkHeader>

          {(authState.role === 'S' ||
            authState === null ||
            authState.role === null ||
            authState.role === '') && (
            <FooterLink
              href={`${policiesBaseUrl}/privacy`}
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy for Parents
            </FooterLink>
          )}

          {(authState.role === 'S' ||
            authState === null ||
            authState.role === null ||
            authState.role === '') && (
            <FooterLink
              href={`${policiesBaseUrl}/terms`}
              target="_blank"
              rel="noreferrer"
            >
              Terms and Conditions for Parents
            </FooterLink>
          )}

          {(authState.role === 'T' ||
            authState === null ||
            authState.role === null ||
            authState.role === '') && (
            <FooterLink
              href={`${policiesBaseUrl}/tutor/terms`}
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy for Tutors
            </FooterLink>
          )}

          {(authState.role === 'T' ||
            authState === null ||
            authState.role === null ||
            authState.role === '') && (
            <FooterLink
              href={`${policiesBaseUrl}/tutor/terms`}
              target="_blank"
              rel="noreferrer"
            >
              Terms and Conditions for Tutors
            </FooterLink>
          )}

          <FooterLink
            href={`${policiesBaseUrl}/terms#refund`}
            target="_blank"
            rel="noreferrer"
          >
            Cancellation and Refunds
          </FooterLink>

          <FooterLinkHeader>Address</FooterLinkHeader>
          <FooterAddress>196 - F, First Floor</FooterAddress>
          <FooterAddress>
            Savitri Nagar, Near Panchsheel Park Metro Station New Delhi - 110017
          </FooterAddress>
        </ContentContainerOther>
      </ContentContainer>
    </Container>
  )
}

export default HelpAndSupport
